package com.mps.asc_app.ui.components

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.semantics.Role
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import com.mps.asc_app.ui.theme.AscMuted
import com.mps.asc_app.ui.theme.AscNavy
import com.mps.asc_app.ui.theme.SlotColors

/** Darstellung einer Platz-Kachel (Redesign Stufe 2). */
enum class SlotTileKind { FREE, BOOKED, MINE, BLOCKED }

data class SlotTileText(val kind: SlotTileKind, val title: String, val subtitle: String?)

/** Titel und Unterzeile einer Kachel; eigene Buchungen erkennt man an canCancel. */
fun slotTileText(slot: UiSlot): SlotTileText {
    val kind = when (slot.status) {
        UiSlotStatus.FREE -> SlotTileKind.FREE
        UiSlotStatus.BOOKED -> if (slot.canCancel) SlotTileKind.MINE else SlotTileKind.BOOKED
        UiSlotStatus.BLOCKED -> SlotTileKind.BLOCKED
    }
    val title = when (kind) {
        SlotTileKind.FREE -> "Frei"
        SlotTileKind.MINE -> "Deine Buchung"
        SlotTileKind.BOOKED -> slot.title?.takeIf { it.isNotBlank() } ?: "Belegt"
        SlotTileKind.BLOCKED -> slot.title?.takeIf { it.isNotBlank() } ?: "Gesperrt"
    }
    val names = slot.participantNames.filter { it.isNotBlank() }.joinToString(", ").takeIf { it.isNotEmpty() }
    val subtitle = when (kind) {
        SlotTileKind.FREE -> "Tippen zum Buchen"
        SlotTileKind.BLOCKED -> slot.blockedComment?.takeIf { it.isNotBlank() } ?: slot.subtitle
        else -> names ?: slot.subtitle
    }
    return SlotTileText(kind, title, subtitle)
}

private fun colorsFor(kind: SlotTileKind): Triple<Color, Color, BorderStroke?> = when (kind) {
    SlotTileKind.FREE -> Triple(SlotColors.FreeContainer, SlotColors.FreeContent, BorderStroke(1.dp, SlotColors.FreeBorder))
    SlotTileKind.BOOKED -> Triple(SlotColors.BookedContainer, SlotColors.BookedContent, null)
    SlotTileKind.MINE -> Triple(AscNavy, Color.White, null)
    SlotTileKind.BLOCKED -> Triple(SlotColors.BlockedContainer, SlotColors.BlockedContent, null)
}

/** Stunden untereinander, je Zeile Platz 1 und Platz 2 als große Kacheln nebeneinander. */
@Composable
fun CourtSlotTiles(
    courts: List<String>,
    rows: List<UiRow>,
    onSlotClick: (UiSlot) -> Unit,
    modifier: Modifier = Modifier,
) {
    Column(modifier = modifier, verticalArrangement = Arrangement.spacedBy(8.dp)) {
        Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
            Box(Modifier.width(48.dp))
            courts.take(2).forEach { court ->
                Text(
                    text = court,
                    style = MaterialTheme.typography.labelMedium,
                    color = AscMuted,
                    modifier = Modifier.weight(1f),
                )
            }
        }
        rows.forEach { row ->
            Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                Text(
                    text = row.timeLabel,
                    style = MaterialTheme.typography.titleSmall,
                    modifier = Modifier.width(48.dp).padding(top = 14.dp),
                )
                SlotTile(row.left, onClick = { onSlotClick(row.left) }, modifier = Modifier.weight(1f))
                SlotTile(row.right, onClick = { onSlotClick(row.right) }, modifier = Modifier.weight(1f))
            }
        }
    }
}

@Composable
fun SlotTile(slot: UiSlot, onClick: () -> Unit, modifier: Modifier = Modifier) {
    val text = slotTileText(slot)
    val (container, content, border) = colorsFor(text.kind)
    // Gesperrte Slots sind nur klickbar, wenn ein Admin sie gesperrt hat (zum Entsperren/Info).
    val clickable = slot.status != UiSlotStatus.BLOCKED || slot.isAdminBlocked
    Surface(
        modifier = modifier
            .heightIn(min = 58.dp)
            .clickable(enabled = clickable, role = Role.Button, onClick = onClick),
        shape = RoundedCornerShape(12.dp),
        color = container,
        contentColor = content,
        border = border,
    ) {
        Column(
            modifier = Modifier.padding(horizontal = 12.dp, vertical = 8.dp),
            verticalArrangement = Arrangement.Center,
        ) {
            Text(
                text = text.title,
                style = MaterialTheme.typography.bodyMedium,
                fontWeight = FontWeight.SemiBold,
                maxLines = 1,
                overflow = TextOverflow.Ellipsis,
            )
            text.subtitle?.let {
                Text(
                    text = it,
                    style = MaterialTheme.typography.bodySmall,
                    color = content.copy(alpha = 0.85f),
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis,
                )
            }
        }
    }
}

/** Legende über den Kacheln: frei, belegt, deine, gesperrt. */
@Composable
fun SlotLegend(modifier: Modifier = Modifier) {
    Row(modifier = modifier, horizontalArrangement = Arrangement.spacedBy(12.dp)) {
        listOf(SlotTileKind.FREE to "frei", SlotTileKind.BOOKED to "belegt", SlotTileKind.MINE to "deine", SlotTileKind.BLOCKED to "gesperrt")
            .forEach { (kind, label) ->
                val (container, _, border) = colorsFor(kind)
                Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(6.dp)) {
                    Box(
                        Modifier
                            .size(10.dp)
                            .background(container, RoundedCornerShape(3.dp))
                            .then(if (border != null) Modifier.border(border, RoundedCornerShape(3.dp)) else Modifier)
                    )
                    Text(label, style = MaterialTheme.typography.labelSmall, color = AscMuted)
                }
            }
    }
}
